import { getCookie } from './helpers';
import { redirect } from 'react-router-dom';
import { ToastQueue } from '@adobe/react-spectrum';
import { handle401Error, handle403Error } from './authErrorHandler';

export class ResponseError extends Error {
  public response: Response;
  public shouldRetry?: boolean;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response: void | Response) {
  if (response instanceof Response) {
    if (response.status === 204 || response.status === 205) {
      return null;
    }
    return response.json();
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 * @param  {object} [errorHandling] Custom error handling options
 * @param  {boolean} [errorHandling.showCustomError] Whether to enable custom error handling (default: false)
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(
  response: Response,
  errorHandling: {
    showCustomError?: boolean;
  } = {
    showCustomError: false
  }
) {
  // Handle 500 Server Error
  if (response.status === 500 && !errorHandling?.showCustomError) {
    ToastQueue.negative('Server error occurred. Please try again later.', {
      timeout: 5000
    });
    return redirect('/server-error');
  }

  // Handle 401 Unauthorized - use centralized handler
  if (response.status === 401 && !errorHandling?.showCustomError) {
    handle401Error(`API call to ${response.url}`);
    // Return response to prevent further processing
    return response;
  }

  // Handle 403 Forbidden - use centralized handler
  if (response.status === 403 && !errorHandling?.showCustomError) {
    handle403Error(`API call to ${response.url}`);
    const err = new ResponseError(response);
    err.response = response;
    throw err;
  }

  if (response.status >= 200 && response.status < 300) return response;
  if (response.status === 400) return response;

  // Handle 404 errors - don't retry these
  if (response.status === 404) {
    console.warn('API endpoint not found (404):', response.url);
    const err = new ResponseError(response);
    err.response = response;
    err.shouldRetry = false; // Mark as non-retryable
    throw err;
  }

  const err = new ResponseError(response);
  err.response = response;

  throw err;
}

async function postDataMethod(url: string = '', data?: any, options?: any) {
  // Default options are marked with *
  const headers = { authorization: `Bearer ${getCookie('app_access_token')}` };

  if (!options.contentType) {
    headers['Content-Type'] = 'application/json';
    // eslint-disable-next-line no-param-reassign
    data = JSON.stringify(data);
  }

  const response = await fetch(url, {
    method: options.method, // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    headers,
    body: data // body data type must match "Content-Type" header
  });
  return response;
}

/**
 * Requests a URL, returning a promise.
 *
 * Authorization semantics (changed 2026-05-25): if the caller passes an
 * `Authorization` header in `options.headers`, it is **respected as-is** —
 * this util will no longer overwrite it. If the caller does not pass one,
 * this util falls back to `Bearer <app_access_token cookie>`. This is the
 * "caller-wins" model.
 *
 * The previous behaviour was to unconditionally set `authorization` (lower
 * case) on top of any header the caller supplied, which — when the caller
 * passed `Authorization` (capital A) — produced a comma-joined
 * `Authorization: Bearer X, Bearer X` that strict gateways 401'd on. All
 * current callers that supply Authorization themselves do so by reading the
 * same `app_access_token` cookie, so the effective header value is unchanged
 * for them. The escape hatch (passing a different token, e.g. a PAT for a
 * future debug tool) is a deliberate by-product and not relied on today.
 *
 * Caller's `options` object is not mutated. The function shallow-copies into
 * `fetchOptions` and reassigns `fetchOptions.headers`; a caller that still
 * holds a reference to its original `options.headers` plain object sees it
 * post-call unchanged. Internally we use the `Headers` API transiently for
 * case-insensitive dedup, then convert back to a plain object via
 * `Object.fromEntries(headers.entries())` before assignment — this preserves
 * the previous contract for any test mock / interceptor / logger that reads
 * `fetchOptions.headers['Authorization']` via bracket notation. Header names
 * end up lowercased (a side effect of `Headers` normalization), but values
 * are intact and bracket access works for any consumer that knows to use the
 * canonical lowercased key (HTTP is case-insensitive on the wire anyway).
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @param  {object} [errorHandling] Custom error handling options
 * @param  {boolean} [errorHandling.showCustomError] Whether to enable custom error handling (default: false)
 * @param  {Function} [navigate] Navigation function
 *
 * @return {object}           The response data
 */
export default async function request(
  url: string,
  options?: RequestInit,
  errorHandling: {
    showCustomError?: boolean;
  } = {
    showCustomError: false
  },
  navigate?: Function
): Promise<{} | { err: ResponseError }> {
  // // Temporary mock for testing
  // if (url.includes('moments')) {
  //   const mockResponse = await fetch('/moment.json');
  //   const mockData = await mockResponse.json();
  //   return mockData;
  // }
  const fetchOptions = { ...options };
  // Use the Headers API so case-insensitive dedup is automatic.
  // Previously this util unconditionally set `authorization` (lowercase) while
  // callers like tokensApi.ts pass `Authorization` (capital A) in their headers
  // object — fetch then serializes both keys, producing a comma-joined
  // `Authorization: Bearer X, Bearer X` header that strict gateways reject as
  // unauthenticated. Only set Authorization here if the caller didn't.
  // Use the Headers API transiently for case-insensitive dedup, then convert
  // back to a plain object before assignment. fetch accepts either shape, but
  // keeping `fetchOptions.headers` as a plain object preserves the previous
  // contract for test mocks / request interceptors / logging middleware that
  // read headers via bracket notation (e.g. `options.headers['Authorization']`)
  // — that would silently return `undefined` against a Headers instance.
  const headers = new Headers(
    (fetchOptions.headers as HeadersInit) || undefined
  );
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${getCookie('app_access_token')}`);
  }
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  fetchOptions.headers = Object.fromEntries(headers.entries());

  const fetchResponse = await fetch(url, fetchOptions);
  const response = checkStatus(fetchResponse, errorHandling);
  return parseJSON(response);
}

export async function getErrorResponse(e) {
  const err = await e.response.json();
  return err;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export function postData(url, data, options) {
  return postDataMethod(url, data, options).then(checkStatus).then(parseJSON);
}
